import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  fetchPublicCollections,
  getCurrentUser,
  checkSubscriptionsBatch,
  updateMasteryStage,
} from "../../api";
import CollectionPreviewModal from "../../components/CollectionPreviewModal";
import { useAuth0 } from "@auth0/auth0-react";
import { Collection as APICollection } from "../../api";
import axios from "axios";
import { contrastingTextColor, lightenColor } from "../../utils/colorUtils";
import { humanDisplayName } from "../../utils/displayName";
import { collectionColorSchemes, DEFAULT_CARD_BACKGROUND } from "../../constants/colorSchemes";
import { useTheme } from "../../context/ThemeContext";
import { tourStepsDiscoverCollections } from "./tourStepsDiscoverCollections";
import GuidedTour from "../../components/GuidedTour";
import { VisibilityStates } from "../../types/VisibilityStates";
import { getCategoryBadgeStyle } from "../../constants/categoryColors";
import CategoryBadge from "../../components/CategoryBadge";
import BubbleText from "../../components/BubbleText";
import {
  USER_MASTERY_STAGES,
  USER_MASTERY_STAGE_LABELS,
  UserMasteryStage,
  collectionMatchesUserMasteryStage,
  getCollectionStageLabel,
  normalizeCollectionStage,
  normalizeUserMasteryStage,
  readStoredMasteryStage,
  writeStoredMasteryStage,
} from "../../constants/collectionStages";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import SortableGridItem, {
  gridCollisionDetection,
} from "../../components/SortableGridItem";
import CollectionCardGrid from "../../components/CollectionCardGrid";

/**
 * @fileoverview Component for discovering and previewing public collections.
 * 
 * @component DiscoverCollections
 * @description Allows users to browse, search, and preview public collections
 * with sorting and filtering capabilities.
 * 
 * @typedef {Object} Collection
 * @property {string} collection_id - Unique identifier for the collection
 * @property {string} name - Collection name
 * @property {string} description - Collection description/items JSON
 * @property {string} creator_display_name - Display name of collection creator
 * @property {string} category - Collection category
 * @property {string} created_at - Creation timestamp
 * @property {number} item_count - Number of items in collection
 * 
 * Features:
 * - All public collections in one grid
 * - Live search by name, creator, or category
 * - Category filter chips
 * - Multiple sort options (date, alphabetical, item count, custom)
 * - Collection preview functionality
 * - Subscription status tracking
 * - Theme-aware styling
 * - Loading states
 * - Guided tour integration
 * 
 * Sort Options:
 * - Date (newest first)
 * - Alphabetical (A-Z)
 * - Item Count (most to least)
 * - Custom Order
 */

interface Item {
  id: number;
  name: string;
  svg?: string;
  count?: number;
}

interface Collection extends Omit<APICollection, "items"> {
  items: Item[];
  item_count?: number;
}

const CUSTOM_ORDER_STORAGE_KEY = "discoverCollectionsCustomOrder";
const KNOWN_CATEGORIES = [
  "Math",
  "Language Arts",
  "Number Sense",
  "Science",
  "Nursing",
  "Domino Comparison",
  "Picture Quiz",
  "Picture Words",
];

const loadCustomOrder = (): number[] => {
  try {
    const raw = localStorage.getItem(CUSTOM_ORDER_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(Number) : [];
  } catch {
    return [];
  }
};

const saveCustomOrder = (orderedCollections: Collection[]) => {
  localStorage.setItem(
    CUSTOM_ORDER_STORAGE_KEY,
    JSON.stringify(orderedCollections.map((collection) => collection.collection_id)),
  );
};

const applyCustomOrder = (
  collectionsToOrder: Collection[],
  order: number[],
): Collection[] => {
  if (order.length === 0) {
    return collectionsToOrder;
  }
  const byId = new Map(
    collectionsToOrder.map((collection) => [collection.collection_id, collection]),
  );
  const ordered: Collection[] = [];
  for (const id of order) {
    const collection = byId.get(id);
    if (collection) {
      ordered.push(collection);
      byId.delete(id);
    }
  }
  byId.forEach((collection) => ordered.push(collection));
  return ordered;
};

const DiscoverCollections: React.FC = () => {
  const { user } = useAuth0();
  const { theme, adjustColorForColorblindness } = useTheme();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [filteredCollections, setFilteredCollections] = useState<Collection[]>([]);
  const [activeCollection, setActiveCollection] = useState<Collection | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortOption, setSortOption] = useState<string>(() => {
    return localStorage.getItem("sortOption") || "date";
  });
  const { getAccessTokenSilently } = useAuth0();
  const [subscriptionStatus, setSubscriptionStatus] = useState<
    Record<string, boolean>
  >({});
  const [visibilityStates, setVisibilityStates] = useState<VisibilityStates>({
    isSearchInputVisible: true,
    isSortSelectVisible: true,
    isCollectionsGridVisible: true,
    isPreviewButtonVisible: true,
    isDotCountTypeVisible: false,
    isMinDotsVisible: false,
    isMaxDotsVisible: false,
    isTypeSelectVisible: false,
    isItemCountVisible: false,
    isCollectionItemCountVisible: false,
    isDotColorVisible: false,
    isDotShapeVisible: false,
    isGenerateRandomSequenceButtonVisible: false,
    isFileUploadVisible: false,
    isClearButtonVisible: false,
    isGeneratedSequencePreviewVisible: false,
    isNameInputVisible: false,
    isAddNameButtonVisible: false,
    isSpinButtonVisible: false,
    isNamesListVisible: false,
    isCollectionNameVisible: false,
    isCategorySelectVisible: true,
    isStageSelectVisible: false,
    isPublicCheckboxVisible: false,
    isSubmitButtonVisible: false,
    isCollectionCardVisible: false,
    isStartCollectionButtonVisible: false,
    isEditCollectionButtonVisible: false,
    isDeleteCollectionButtonVisible: false,
    isSessionSettingsModalVisible: false,
    isEditCollectionModalVisible: false,
    isDuplicateCollectionModalVisible: false,
    isCollectionPreviewModalVisible: false,
    isNextButtonVisible: false,
  });

  const [isTourRunning, setIsTourRunning] = useState<boolean>(false);
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);
  const [userDisplayName, setUserDisplayName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>("All Collections");
  const [myMasteryStage, setMyMasteryStage] = useState<UserMasteryStage>(() =>
    readStoredMasteryStage(),
  );
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Define the steps variable
  const steps = tourStepsDiscoverCollections(visibilityStates); // Create tour steps based on visibility states

  // Add a function to start the tour
  const startTour = () => {
    const tourCompleted = localStorage.getItem("tourCompleted");
    if (!tourCompleted) {
      setIsTourRunning(true);
      setCurrentTourStep(0); // Reset to the first step
    }
  };

  useEffect(() => {
    // Start the tour when the component mounts
    startTour();
  }, []);

  // Example of updating visibility states based on some logic
  useEffect(() => {
    // You can set visibility states based on your application logic
    setVisibilityStates((prevStates) => ({
      ...prevStates, // Keep the previous state
      isSearchInputVisible: true,
      isSortSelectVisible: true,
      isCategorySelectVisible: true,
      isCollectionsGridVisible: collections.length > 0,
      isPreviewButtonVisible: activeCollection !== null,
    }));
  }, [collections, activeCollection]); // Dependencies to trigger updates

  const handleTourStepChange = (step: number) => {
    setCurrentTourStep(step);
  };

  const handleTourComplete = () => {
    console.log("Tour completed");
    setIsTourRunning(false); // Reset the tour running state
  };

  const fetchCollections = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedCollections = await fetchPublicCollections();
      const collectionsWithItems =
        fetchedCollections?.map((collection) => ({
          ...collection,
          items: parseDescription(collection.description),
        })) || [];

      // Batch check subscriptions
      const collectionIds = collectionsWithItems.map((c) => c.collection_id);
      const subscriptionResults = await checkSubscriptionsBatch(
        collectionIds,
        getAccessTokenSilently,
      );

      setSubscriptionStatus(subscriptionResults);
      setCollections(collectionsWithItems);
    } catch (error) {
      console.error("Error fetching public collections:", error);
      if (axios.isAxiosError(error)) {
        console.error("Axios error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          headers: error.response?.headers,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const parseDescription = (description: string): Item[] => {
    try {
      return JSON.parse(description).map(
        (
          item: { name: string; svg?: string; count?: number },
          index: number,
        ) => ({
          id: index,
          name: item.name,
          svg: item.svg,
          count: item.count,
        }),
      );
    } catch {
      // If parsing fails, treat the description as a single item
      return [{ id: 0, name: description }];
    }
  };

  const collectionMatchesSearch = useCallback(
    (collection: Collection, query: string) => {
      const normalizedQuery = query.trim().toLowerCase();
      if (!normalizedQuery) {
        return true;
      }
      const creatorName = humanDisplayName(collection.creator_display_name);
      return (
        collection.name.toLowerCase().includes(normalizedQuery) ||
        creatorName.toLowerCase().includes(normalizedQuery) ||
        (collection.category || "").toLowerCase().includes(normalizedQuery)
      );
    },
    [],
  );

  const openModal = async (collection: APICollection) => {
    console.log("Opening collection preview");
    const parsedCollection: Collection = {
      ...collection,
      items: parseDescription(collection.description),
    };

    // Use the subscription status from the state
    const isSubscribed = subscriptionStatus[collection.collection_id] || false;
    setActiveCollection({ ...parsedCollection, isSubscribed });
  };

  const closeModal = () => setActiveCollection(null);

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newSortOption = event.target.value;
    setSortOption(newSortOption);
    localStorage.setItem("sortOption", newSortOption);
    if (newSortOption === "custom" && loadCustomOrder().length === 0) {
      const startingOrder =
        filteredCollections.length > 0 ? filteredCollections : collections;
      saveCustomOrder(startingOrder);
    }
  };

  const getItemsCount = (description: string): number => {
    if (!description) return 0;
    try {
      const items = JSON.parse(description);
      return Array.isArray(items) ? items.length : 0;
    } catch {
      console.error("Error parsing description:", description);
      return 0;
    }
  };

  const sortCollections = useCallback(
    (collectionsToSort: Collection[]) => {
      switch (sortOption) {
        case "date":
          return [...collectionsToSort].sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          );
        case "alphabetical":
          return [...collectionsToSort].sort((a, b) =>
            a.name.localeCompare(b.name),
          );
        case "itemCount":
          return [...collectionsToSort].sort(
            (a, b) => getItemsCount(b.description) - getItemsCount(a.description)
          );
        case "custom":
          return applyCustomOrder(collectionsToSort, loadCustomOrder());
        default:
          return collectionsToSort;
      }
    },
    [sortOption],
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || sortOption !== "custom") {
      return;
    }

    const oldIndex = filteredCollections.findIndex(
      (collection) => collection.collection_id.toString() === String(active.id),
    );
    const newIndex = filteredCollections.findIndex(
      (collection) => collection.collection_id.toString() === String(over.id),
    );
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
      return;
    }

    const reordered = arrayMove(filteredCollections, oldIndex, newIndex);
    setFilteredCollections(reordered);

    const visibleIds = new Set(
      reordered.map((collection) => collection.collection_id),
    );
    let nextVisibleIndex = 0;
    const merged = collections.map((collection) =>
      visibleIds.has(collection.collection_id)
        ? reordered[nextVisibleIndex++]
        : collection,
    );
    setCollections(merged);
    saveCustomOrder(merged);
  };

  const adjustColorForTheme = useCallback(
    (color: string) => {
      return adjustColorForColorblindness(color);
    },
    [adjustColorForColorblindness],
  );

  const handleSubscribe = (collectionId: string) => {
    setSubscriptionStatus((prev) => ({
      ...prev,
      [collectionId]: true,
    }));
  };

  useEffect(() => {
    const fetchUserDisplayName = async () => {
      if (user) {
        try {
          const userData = await getCurrentUser(getAccessTokenSilently);
          setUserDisplayName(humanDisplayName(userData.display_name, user.name, user.email));
          const userStage = normalizeUserMasteryStage(
            (userData as { mastery_stage?: string }).mastery_stage,
          );
          setMyMasteryStage(userStage);
          writeStoredMasteryStage(userStage);
        } catch (error) {
          console.error("Error fetching user display name:", error);
          setUserDisplayName(user.name || ""); // Fallback to user.name if fetch fails
        }
      }
    };
    fetchUserDisplayName();
  }, [user, getAccessTokenSilently]);

  const searchMatchedCollections = useMemo(
    () =>
      collections.filter((collection) =>
        collectionMatchesSearch(collection, searchQuery),
      ),
    [collections, collectionMatchesSearch, searchQuery],
  );

  const categories = useMemo(() => {
    const present = new Set(
      collections.map((collection) => collection.category || "Uncategorized"),
    );
    const extras = [...present]
      .filter((category) => !KNOWN_CATEGORIES.includes(category))
      .sort((a, b) => a.localeCompare(b));
    return ["All Collections", ...KNOWN_CATEGORIES, ...extras];
  }, [collections]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {
      "All Collections": searchMatchedCollections.length,
    };
    for (const collection of searchMatchedCollections) {
      const category = collection.category || "Uncategorized";
      counts[category] = (counts[category] || 0) + 1;
    }
    return counts;
  }, [searchMatchedCollections]);

  const filterAndSortCollections = useCallback(
    (
      collectionsToFilter: Collection[],
      category: string,
      stageFilter: UserMasteryStage = myMasteryStage,
    ) => {
      let filtered = collectionsToFilter.filter((collection) =>
        collectionMatchesSearch(collection, searchQuery),
      );

      if (category !== "All Collections") {
        filtered = filtered.filter(
          (collection) => (collection.category || "Uncategorized") === category,
        );
      }

      filtered = filtered.filter((collection) =>
        collectionMatchesUserMasteryStage(collection.stage, stageFilter),
      );

      return sortCollections(filtered);
    },
    [collectionMatchesSearch, searchQuery, sortCollections, myMasteryStage],
  );

  const groupedCollections = useMemo(() => {
    const groups: Record<string, Collection[]> = {};
    const categoryOrder: string[] = [];

    for (const collection of filteredCollections) {
      const category = collection.category || "Uncategorized";
      if (!groups[category]) {
        groups[category] = [];
        categoryOrder.push(category);
      }
      groups[category].push(collection);
    }

    return categoryOrder.map((category) => ({
      category,
      collections: groups[category],
    }));
  }, [filteredCollections]);

  const hasActiveFilters =
    searchQuery.trim() !== "" ||
    selectedCategory !== "All Collections" ||
    myMasteryStage !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("All Collections");
    setMyMasteryStage("all");
    writeStoredMasteryStage("all");
    void updateMasteryStage("all", getAccessTokenSilently).catch(() => undefined);
  };

  useEffect(() => {
    setFilteredCollections(
      filterAndSortCollections(collections, selectedCategory, myMasteryStage),
    );
  }, [collections, selectedCategory, myMasteryStage, filterAndSortCollections]);

  const renderCollectionCard = (
    collection: Collection,
    index: number,
  ) => {
    const baseColor = adjustColorForTheme(
      collectionColorSchemes[index % collectionColorSchemes.length]
        .backgroundColor,
    );
    const lightColor = baseColor ? lightenColor(baseColor, 0.7) : "";
    const itemCount =
      collection.item_count ?? collection.items?.length ?? 0;

    return (
      <div
        className={`collection-card min-w-0 w-full rounded-lg border-4 p-4 shadow-lg ${
          sortOption === "custom" ? "cursor-grab active:cursor-grabbing" : ""
        }`}
        style={{
          backgroundColor: adjustColorForColorblindness(
            theme.cardBackgroundColor || DEFAULT_CARD_BACKGROUND,
          ),
        }}
      >
        <div className="flex size-full flex-col">
          <h2
            className="border-5 w-full rounded-t-lg border-b-0 border-black p-2.5 text-center text-xl font-bold"
            style={{
              backgroundColor: baseColor,
              color: contrastingTextColor(baseColor),
            }}
          >
            {collection.name}
          </h2>
          <div
            className="border-5 flex size-full flex-col rounded-b-lg border-black"
            style={{
              backgroundColor: lightColor,
              transition: "background-color 0.3s ease",
            }}
          >
            <div className="flex size-full flex-col p-4">
              <div className="mb-auto text-center">
                <p className="mb-1 text-base font-bold text-black">
                  {itemCount} {itemCount === 1 ? "item" : "items"} in
                  collection
                </p>
                <p className="mb-1 text-sm text-black">
                  Created by:{" "}
                  {humanDisplayName(collection.creator_display_name)}
                </p>
                <p className="mb-1 text-sm text-black">
                  Category: <CategoryBadge category={collection.category} />
                  {normalizeCollectionStage(collection.stage) !== "none" && (
                    <span className="ml-2 inline-block rounded bg-teal-700 px-2 py-0.5 text-xs font-semibold text-white">
                      {getCollectionStageLabel(collection.stage)}
                    </span>
                  )}
                </p>
              </div>
              <div className="mt-auto pt-4">
                <button
                  type="button"
                  className="preview-collection-button w-full cursor-pointer rounded-lg border-4 border-black p-2 text-base font-bold text-black transition-all duration-300 hover:scale-105 active:scale-95"
                  style={{ backgroundColor: baseColor }}
                  onClick={() => openModal(collection)}
                >
                  {subscriptionStatus[collection.collection_id]
                    ? "Already in My Collections"
                    : "Preview Collection"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`page-container mt-6 text-theme-text`}>
      <h1 className="discover-collections-page inherit mb-4 text-2xl font-bold sm:text-3xl">
        <BubbleText>Discover Public Collections</BubbleText>
      </h1>
      {user && <p className="inherit mb-4">Welcome, {userDisplayName}</p>}
      
      <div className="mb-4 flex w-full flex-col items-stretch justify-center gap-2 sm:flex-row sm:items-center">
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, creator, or category"
          className="search-collections-input w-full rounded-md border border-black bg-white p-2 text-black sm:max-w-md"
          aria-label="Search public collections"
        />
        <div className="flex items-center justify-center">
          <label htmlFor="sortSelect" className="inherit mr-2 font-bold">
            Sort by:
          </label>
          <select
            id="sortSelect"
            value={sortOption}
            onChange={handleSortChange}
            className="sort-collections-select min-h-[44px] w-full rounded-md border border-black bg-white p-2 text-black sm:w-auto"
            aria-label="Sort collections"
          >
            <option value="date">Date</option>
            <option value="alphabetical">Alphabetical</option>
            <option value="itemCount">Item Count</option>
            <option value="custom">Custom Order</option>
          </select>
        </div>
      </div>

      <div className="mb-4 flex flex-col items-center justify-center gap-1">
        <label htmlFor="discoverMasteryStageSelect" className="inherit font-bold">
          My stage:
        </label>
        <select
          id="discoverMasteryStageSelect"
          value={myMasteryStage}
          onChange={(e) => {
            const next = normalizeUserMasteryStage(e.target.value);
            setMyMasteryStage(next);
            writeStoredMasteryStage(next);
            void updateMasteryStage(next, getAccessTokenSilently).catch(
              (error) => console.error("Error saving mastery stage:", error),
            );
          }}
          className="min-h-[44px] rounded-md border border-black bg-white p-2 text-black"
          aria-label="Filter public collections by your mastery stage"
        >
          {USER_MASTERY_STAGES.map((value) => (
            <option key={value} value={value}>
              {USER_MASTERY_STAGE_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      <div
        className="discover-category-filters mb-4 flex flex-wrap justify-center gap-2"
        role="group"
        aria-label="Filter collections by category"
      >
        {categories
          .filter(
            (category) =>
              category === "All Collections" ||
              category === selectedCategory ||
              (categoryCounts[category] ?? 0) > 0,
          )
          .map((category) => {
          const isSelected = selectedCategory === category;
          const count = categoryCounts[category] ?? 0;
          const chipStyle = isSelected
            ? getCategoryBadgeStyle(category)
            : undefined;
          return (
            <button
              key={category}
              type="button"
              onClick={() => setSelectedCategory(category)}
              aria-pressed={isSelected ? "true" : "false"}
              className={`rounded-full border border-black px-3 py-1 text-sm font-bold transition-transform hover:scale-105 ${
                isSelected ? "text-white" : "bg-white text-black"
              }`}
              style={
                chipStyle
                  ? {
                      backgroundColor: chipStyle.backgroundColor,
                      color: chipStyle.color,
                    }
                  : undefined
              }
            >
              {category} ({count})
            </button>
          );
        })}
      </div>

      {!isLoading && (
        <div className="inherit mb-4 flex flex-wrap items-center justify-center gap-3 text-sm">
          <p>
            Showing {filteredCollections.length} of {collections.length}{" "}
            {collections.length === 1 ? "collection" : "collections"}
          </p>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-md border border-black bg-white px-3 py-1 font-bold text-black"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {sortOption === "custom" && !isLoading && (
        <p className="inherit mb-4 text-center text-sm">
          Drag collections to reorder them. Your order is saved on this device.
        </p>
      )}

      {isLoading ? (
        <div className="inherit flex min-h-[200px] items-center justify-center">
          <div className="size-12 animate-spin rounded-full border-b-2 border-current"></div>
        </div>
      ) : filteredCollections.length === 0 ? (
        <p className="inherit py-12 text-center">
          No collections match those filters. Try a different search or
          category.
        </p>
      ) : sortOption === "custom" ? (
        <DndContext
          sensors={dndSensors}
          collisionDetection={gridCollisionDetection}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={filteredCollections.map((collection) =>
              collection.collection_id.toString(),
            )}
            strategy={rectSortingStrategy}
          >
            <div className="grid-container w-full">
              <CollectionCardGrid>
                {filteredCollections.map((collection, index) => (
                  <SortableGridItem
                    key={collection.collection_id}
                    id={collection.collection_id.toString()}
                    className="h-full"
                  >
                    {renderCollectionCard(collection, index)}
                  </SortableGridItem>
                ))}
              </CollectionCardGrid>
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="grid-container w-full">
          {selectedCategory === "All Collections" ? (
            groupedCollections.map(({ category, collections: categoryCollections }) => (
              <div key={category} className="mb-8 w-full">
                <h2 className="inherit mb-4 text-xl font-bold">{category}</h2>
                <CollectionCardGrid>
                  {categoryCollections.map((collection, index) => (
                    <React.Fragment key={collection.collection_id}>
                      {renderCollectionCard(collection, index)}
                    </React.Fragment>
                  ))}
                </CollectionCardGrid>
              </div>
            ))
          ) : (
            <CollectionCardGrid>
              {filteredCollections.map((collection, index) => (
                <React.Fragment key={collection.collection_id}>
                  {renderCollectionCard(collection, index)}
                </React.Fragment>
              ))}
            </CollectionCardGrid>
          )}
        </div>
      )}
      {activeCollection && (
        <CollectionPreviewModal
          collection={activeCollection}
          onClose={closeModal}
          isSubscribed={activeCollection.isSubscribed || false}
          onSubscribe={handleSubscribe}
        />
      )}
      <GuidedTour
        steps={steps}
        isRunning={isTourRunning}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={handleTourStepChange}
        tourName="discoverCollections"
      />
    </div>
  );
};

export default DiscoverCollections;
